import { useState } from 'react';
import { Link } from 'react-router-dom';

const FLEET_VEHICLES = [
  {
    id: 'air',
    type: 'Air Cargo',
    name: 'Sky Fleet',
    tagline: 'Transcontinental & International',
    description: 'Boeing 747 and Airbus A300F cargo aircraft covering 180+ countries with 48-hour global reach.',
    stats: ['180+ countries', '48hr delivery', '120 ton capacity'],
    icon: 'ri-plane-fill',
    color: '#ff6b35',
    badge: 'FASTEST',
    image: 'https://readdy.ai/api/search-image?query=large%20white%20cargo%20aircraft%20with%20bold%20orange%20accent%20stripe%20livery%20soaring%20majestically%20above%20dramatic%20golden%20sunset%20clouds%2C%20professional%20aviation%20photography%2C%20air%20freight%20transport%2C%20high%20altitude%20cruise%20flight%2C%20cinematic%20wide%20angle%204K%20commercial%20shot%2C%20clean%20minimalist%20sky%20background&width=900&height=500&seq=swiftfleet-air-2024&orientation=landscape',
  },
  {
    id: 'truck',
    type: 'Express Trucks',
    name: 'Road Fleet',
    tagline: 'Cross-Country Ground Freight',
    description: 'Modern semi-trucks with GPS tracking, climate control, and hazmat certification for all cargo types.',
    stats: ['48 states', '24/7 dispatch', '40 ton capacity'],
    icon: 'ri-truck-fill',
    color: '#22c55e',
    badge: 'MOST POPULAR',
    image: 'https://readdy.ai/api/search-image?query=modern%20sleek%20express%20delivery%20semi%20truck%20with%20bright%20orange%20black%20branding%20driving%20fast%20on%20empty%20American%20interstate%20highway%20at%20night%2C%20professional%20commercial%20automotive%20photography%2C%20long%20exposure%20light%20trails%20on%20road%2C%20freight%20transport%20vehicle%2C%20cinematic%204K%20quality%20shot&width=900&height=500&seq=swiftfleet-truck-2024&orientation=landscape',
  },
  {
    id: 'van',
    type: 'Delivery Vans',
    name: 'Metro Fleet',
    tagline: 'Urban Last-Mile Delivery',
    description: 'Electric and hybrid vans for eco-friendly city deliveries. Same-day service in 200+ metro areas.',
    stats: ['200+ cities', 'Same-day', 'Carbon neutral'],
    icon: 'ri-car-fill',
    color: '#eab308',
    badge: 'ECO FRIENDLY',
    image: 'https://readdy.ai/api/search-image?query=sleek%20white%20and%20orange%20last-mile%20delivery%20van%20driving%20through%20clean%20modern%20city%20downtown%20environment%2C%20professional%20commercial%20product%20photography%2C%20bright%20daylight%20urban%20backdrop%2C%20contemporary%20fleet%20vehicle%2C%20logistics%20delivery%20van%20in%20city%20street%20setting&width=900&height=500&seq=swiftfleet-van-2024&orientation=landscape',
  },
  {
    id: 'bike',
    type: 'Express Riders',
    name: 'Swift Riders',
    tagline: 'Hyper-Local Fast Delivery',
    description: 'Dedicated motorcycle couriers for urgent, time-sensitive deliveries within 2-hour windows.',
    stats: ['2hr delivery', '500+ riders', 'City-wide'],
    icon: 'ri-motorbike-fill',
    color: '#a855f7',
    badge: '2HR DELIVERY',
    image: 'https://readdy.ai/api/search-image?query=professional%20delivery%20motorcycle%20courier%20in%20orange%20safety%20gear%20with%20cargo%20box%20speeding%20through%20busy%20city%20intersection%20at%20evening%2C%20action%20photography%20with%20cinematic%20motion%20blur%2C%20commercial%20logistics%20courier%20photography%2C%20urban%20expressway%20city%20lights%20background%2C%204K%20high%20resolution&width=900&height=500&seq=swiftfleet-moto-2024&orientation=landscape',
  },
];

export default function FleetSection() {
  const [active, setActive] = useState(0);
  const vehicle = FLEET_VEHICLES[active];

  return (
    <section className="py-24 bg-[#12141f] relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '40px 40px',
      }} />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-[#ff6b35]/10 border border-[#ff6b35]/20 rounded-full px-4 py-1.5 mb-5">
            <i className="ri-fleet-line text-[#ff6b35] text-xs" />
            <span className="text-[#ff6b35] text-xs font-semibold tracking-widest">OUR GLOBAL FLEET</span>
          </div>
          <h2 className="font-heading text-4xl font-bold text-white mb-4">
            Powered by a World-Class
            <span className="text-[#ff6b35]"> Delivery Network</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            From cargo jets to express motorcycles — every shipment matched to the optimal vehicle for speed, size, and destination.
          </p>
        </div>

        {/* Vehicle selector tabs */}
        <div className="flex flex-wrap gap-3 justify-center mb-10">
          {FLEET_VEHICLES.map((v, idx) => (
            <button
              key={v.id}
              onClick={() => setActive(idx)}
              className={`flex items-center gap-2.5 px-5 py-3 rounded-xl border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                active === idx
                  ? 'bg-[#ff6b35]/15 border-[#ff6b35]/40 text-[#ff6b35] scale-105'
                  : 'bg-[#1e2235] border-white/10 text-gray-400 hover:text-white hover:border-white/20'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${v.icon} text-base`} style={{ color: active === idx ? '#ff6b35' : 'inherit' }} />
              </div>
              {v.type}
            </button>
          ))}
        </div>

        {/* Main showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Image */}
          <div className="relative group">
            <div className="w-full h-[380px] rounded-2xl overflow-hidden border border-white/5">
              <img
                src={vehicle.image}
                alt={vehicle.type}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0f1a]/80 via-transparent to-transparent" />
            </div>

            {/* Badge */}
            <div
              className="absolute top-5 left-5 px-3 py-1.5 rounded-lg text-xs font-bold tracking-widest"
              style={{ background: vehicle.color, color: 'white' }}
            >
              {vehicle.badge}
            </div>

            {/* Stats row on image */}
            <div className="absolute bottom-5 left-5 right-5 flex gap-3">
              {vehicle.stats.map((stat) => (
                <div key={stat} className="flex-1 bg-[#0d0f1a]/85 backdrop-blur-sm border border-white/10 rounded-lg px-3 py-2 text-center">
                  <p className="text-white text-xs font-semibold">{stat}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="flex flex-col gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: `${vehicle.color}20`, border: `1.5px solid ${vehicle.color}40` }}
                >
                  <i className={`${vehicle.icon} text-xl`} style={{ color: vehicle.color }} />
                </div>
                <div>
                  <p className="text-gray-400 text-xs font-medium uppercase tracking-widest">{vehicle.tagline}</p>
                  <h3 className="font-heading font-bold text-white text-2xl">{vehicle.name}</h3>
                </div>
              </div>
              <p className="text-gray-400 text-base leading-relaxed">{vehicle.description}</p>
            </div>

            {/* Feature list */}
            <div className="space-y-3">
              {[
                'Real-time GPS tracking with live map updates',
                'Automated status notifications at every checkpoint',
                'Temperature-controlled options available',
                'Proof of delivery with photo & signature capture',
                'Insurance coverage up to $10,000 per shipment',
              ].map((feat) => (
                <div key={feat} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${vehicle.color}20` }}>
                    <i className="ri-check-line text-xs" style={{ color: vehicle.color }} />
                  </div>
                  <span className="text-gray-300 text-sm">{feat}</span>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-2">
              <Link
                to="/track"
                className="px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all cursor-pointer whitespace-nowrap"
                style={{ background: vehicle.color }}
              >
                <i className="ri-map-2-line mr-2" />
                Track a Shipment
              </Link>
              <Link
                to="/contact"
                className="px-6 py-3 rounded-xl text-sm font-semibold text-gray-300 bg-white/5 border border-white/10 hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                Request Quote
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom thumbnails */}
        <div className="grid grid-cols-4 gap-4 mt-10">
          {FLEET_VEHICLES.map((v, idx) => (
            <button
              key={v.id}
              onClick={() => setActive(idx)}
              className={`relative h-24 rounded-xl overflow-hidden border cursor-pointer transition-all group ${
                active === idx ? 'border-[#ff6b35]/60 scale-105' : 'border-white/10 hover:border-white/25'
              }`}
            >
              <img src={v.image} alt={v.type} className="w-full h-full object-cover object-center" />
              <div className={`absolute inset-0 transition-all ${active === idx ? 'bg-black/20' : 'bg-black/55 group-hover:bg-black/35'}`} />
              <div className="absolute bottom-2 left-0 right-0 text-center">
                <span className="text-white text-xs font-semibold">{v.type}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
